import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import InfiniteScroll from 'react-infinite-scroll-component';
import api from '../../services/api';
import { useI18n } from '../../i18n/useI18n';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';

const BACKEND_URL = 'https://mphoto.up.railway.app';
const PINK = '#e86a8a';
const PINK_SOFT = '#ff8ba7';

interface CustomerInfo {
  _id: string;
  email: string;
  name: string;
  galleryId: string;
  galleryImageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface CustomerInfoSetting {
  _id: string;
  userId: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

const CustomerInformation: React.FC = () => {
  const { pages: t, formatMessage } = useI18n();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [setting, setSetting] = useState<CustomerInfoSetting | null>(null);
  const [customers, setCustomers] = useState<CustomerInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [limit] = useState(10);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerInfo | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadSetting = async () => {
    try {
      const response = await api.get('/customer-info/setting');
      setSetting(response.data);
    } catch (error: any) {
      console.error('Error loading setting:', error);
      if (error.response?.status === 404) {
        setSetting({ userId: '', enabled: false } as CustomerInfoSetting);
      } else {
        toast.error('Không thể tải cài đặt');
      }
    }
  };

  const loadCustomers = async (pageNum: number = 1, append: boolean = false) => {
    try {
      if (!append) {
        setLoading(true);
        setCustomers([]);
        setPage(1);
        setHasMore(true);
      } else {
        setLoadingMore(true);
      }

      const response = await api.get('/customer-info/info', {
        params: { page: pageNum, limit },
      });

      if (append) {
        setCustomers((prev) => [...prev, ...response.data.data]);
      } else {
        setCustomers(response.data.data);
      }

      setTotal(response.data.pagination.total);
      setPage(pageNum);
      setHasMore(pageNum < response.data.pagination.totalPages);
    } catch (error: any) {
      console.error('Error loading customers:', error);
      if (!append) {
        toast.error('Không thể tải danh sách khách hàng');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreCustomers = () => {
    if (!loadingMore && hasMore && !loading) {
      loadCustomers(page + 1, true);
    }
  };

  useEffect(() => {
    loadSetting();
    loadCustomers(1, false);
  }, []);

  const handleToggleSetting = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnabled = event.target.checked;
    try {
      setSaving(true);
      const response = await api.put('/customer-info/setting', { enabled: newEnabled });
      setSetting(response.data);
      toast.success('Đã cập nhật cài đặt thành công');
    } catch (error: any) {
      console.error('Error updating setting:', error);
      toast.error('Không thể cập nhật cài đặt');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditDialog = (customer: CustomerInfo) => {
    setEditingCustomer(customer);
    setEditEmail(customer.email);
    setEditName(customer.name);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingCustomer(null);
    setEditEmail('');
    setEditName('');
  };

  const handleSaveEdit = async () => {
    if (!editingCustomer) return;
    if (!editEmail.trim() || !editName.trim()) {
      toast.error('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    try {
      setSaving(true);
      await api.put(`/customer-info/info/${editingCustomer._id}`, {
        email: editEmail.trim(),
        name: editName.trim(),
      });
      toast.success('Đã cập nhật thông tin thành công');
      handleCloseEditDialog();
      loadCustomers(1, false);
    } catch (error: any) {
      console.error('Error updating customer:', error);
      toast.error(error.response?.data?.error || 'Không thể cập nhật thông tin');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t.customerInfo.confirmDelete)) return;

    try {
      setDeleting(id);
      await api.delete(`/customer-info/info/${id}`);
      toast.success('Đã xóa thông tin thành công');
      loadCustomers(1, false);
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      toast.error('Không thể xóa thông tin');
    } finally {
      setDeleting(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      const response = await api.get('/customer-info/export-excel', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'customer-info.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) filename = filenameMatch[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Đã tải file Excel thành công');
    } catch (error: any) {
      console.error('Error exporting Excel:', error);
      toast.error('Không thể tải file Excel');
    } finally {
      setLoading(false);
    }
  };

  const openGallery = (galleryId: string) => {
    window.open(`${BACKEND_URL}/api/results/g/${galleryId}`, '_blank', 'noopener,noreferrer');
  };

  const pillBtnSx = {
    borderRadius: 999,
    textTransform: 'none' as const,
    fontWeight: 800,
    boxShadow: 'none',
  };

  return (
    <UserPageShell>
      <Box sx={{ mb: { xs: 1.5, sm: 2.5 } }}>
        <Typography
          component="h1"
          fontWeight={800}
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.85rem' },
            letterSpacing: '-0.03em',
            lineHeight: 1.2,
          }}
        >
          {t.customerInfo.title}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ mt: 0.4, fontSize: { xs: '0.8rem', sm: '0.95rem' }, lineHeight: 1.4 }}
        >
          {t.customerInfo.subtitle}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2.25 },
          mb: { xs: 1.5, sm: 2.5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha(PINK_SOFT, 0.28),
          background: `linear-gradient(160deg, ${alpha(PINK_SOFT, 0.1)} 0%, #fff 72%)`,
        }}
      >
        <Typography fontWeight={800} sx={{ fontSize: { xs: '0.9rem', sm: '1.05rem' }, mb: 0.5 }}>
          {t.customerInfo.settingTitle}
        </Typography>
        <FormControlLabel
          sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
          control={
            <Switch
              checked={setting?.enabled || false}
              onChange={handleToggleSetting}
              disabled={saving || !setting}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              sx={{ mt: 0.25 }}
            />
          }
          label={
            <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.9rem' }, lineHeight: 1.4, fontWeight: 600 }}>
              {setting?.enabled ? t.customerInfo.enabled : t.customerInfo.disabled}
            </Typography>
          }
        />
      </Paper>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: { xs: 1.25, sm: 2 },
        }}
      >
        <Typography fontWeight={800} sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' }, minWidth: 0 }}>
          {formatMessage(t.customerInfo.listTitle, { total })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
          <IconButton
            onClick={() => {
              loadSetting();
              loadCustomers(1, false);
            }}
            disabled={loading}
            aria-label={t.common.refresh}
            sx={{
              width: 40,
              height: 40,
              border: '1px solid',
              borderColor: alpha(PINK, 0.35),
              color: PINK,
              bgcolor: '#fff',
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          {isMobile ? (
            <IconButton
              onClick={handleExportExcel}
              disabled={loading || total === 0}
              aria-label={t.customerInfo.export}
              sx={{
                width: 40,
                height: 40,
                bgcolor: PINK,
                color: '#fff',
                '&:hover': { bgcolor: '#d45a7a' },
                '&.Mui-disabled': { bgcolor: alpha(PINK, 0.3), color: '#fff' },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              disabled={loading || total === 0}
              sx={pillBtnSx}
            >
              {t.customerInfo.export}
            </Button>
          )}
        </Box>
      </Box>

      {loading && customers.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: PINK }} />
        </Box>
      ) : customers.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <PersonIcon sx={{ fontSize: 52, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary" fontWeight={700}>
            {t.customerInfo.empty}
          </Typography>
        </Paper>
      ) : (
        <InfiniteScroll
          dataLength={customers.length}
          next={loadMoreCustomers}
          hasMore={hasMore}
          loader={
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={28} sx={{ color: PINK }} />
            </Box>
          }
          endMessage={
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2, fontWeight: 600, fontSize: '0.8rem' }}
            >
              {formatMessage(t.customerInfo.endMessage, { total })}
            </Typography>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.25 } }}>
            {customers.map((customer, index) => (
              <Paper
                key={customer._id}
                elevation={0}
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: alpha(PINK_SOFT, 0.22),
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.5 },
                  ...userCardEnterSx(index),
                }}
              >
                <Box
                  onClick={() => customer.galleryId && openGallery(customer.galleryId)}
                  sx={{
                    width: { xs: 64, sm: 88 },
                    height: { xs: 64, sm: 72 },
                    flexShrink: 0,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: alpha(PINK_SOFT, 0.1),
                    border: '1px solid',
                    borderColor: alpha(PINK_SOFT, 0.22),
                    cursor: customer.galleryId ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {customer.galleryImageUrl ? (
                    <Box
                      component="img"
                      src={customer.galleryImageUrl}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <ImageIcon sx={{ color: alpha(PINK, 0.4), fontSize: 28 }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    fontWeight={800}
                    noWrap
                    title={customer.name}
                    sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' }, color: '#2d2430' }}
                  >
                    {customer.name || '—'}
                  </Typography>
                  <Typography
                    title={customer.email}
                    sx={{
                      fontSize: { xs: '0.72rem', sm: '0.8rem' },
                      color: alpha(PINK, 0.75),
                      fontWeight: 600,
                      wordBreak: 'break-all',
                      lineHeight: 1.35,
                    }}
                  >
                    {customer.email}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEditDialog(customer)}
                    sx={{ color: PINK }}
                    aria-label={t.common.edit}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(customer._id)}
                    disabled={deleting === customer._id}
                    sx={{ color: '#e53935' }}
                    aria-label={t.common.delete}
                  >
                    {deleting === customer._id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        </InfiniteScroll>
      )}

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1.5, sm: 2 },
            width: { xs: 'calc(100% - 24px)', sm: '100%' },
            borderRadius: 3.5,
            overflow: 'hidden',
            backgroundImage: `linear-gradient(180deg, ${alpha(PINK_SOFT, 0.12)} 0%, #fff 28%)`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.1rem', sm: '1.3rem' },
            letterSpacing: '-0.02em',
            pt: 2.25,
            px: { xs: 2, sm: 3 },
          }}
        >
          {t.customerInfo.editDialog}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, mt: 0.5 }}>
            <TextField
              label={t.customerInfo.colEmail}
              type="email"
              fullWidth
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: alpha(PINK_SOFT, 0.06) } }}
            />
            <TextField
              label={t.customerInfo.colName}
              fullWidth
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: alpha(PINK_SOFT, 0.06) } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCloseEditDialog}
            disabled={saving}
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            {t.common.cancel}
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={saving}
            fullWidth={isMobile}
            sx={pillBtnSx}
          >
            {saving ? <CircularProgress size={18} /> : t.common.save}
          </Button>
        </DialogActions>
      </Dialog>
    </UserPageShell>
  );
};

export default CustomerInformation;
